import { useEffect, useState } from "react";
import { Bell, Bluetooth, MoreHorizontal, Signal, Volume2, Wifi } from "lucide-react";
import { CarScreenViewport } from "./CarScreenViewport";
import { ParcelDayReminderCard } from "./ParcelDayReminderCard";
import { assetUrl } from "../../utils/assetUrl";
import type { ParcelReminderState, ParcelReminderEvent } from "../../logic/parcelReminderMachine";
import "./ParcelDayDesktop.css";

export function ParcelDayDesktop({ model, onEvent }: {
  model: ParcelReminderState;
  onEvent: (event: ParcelReminderEvent) => void;
}) {
  const [captionVisible, setCaptionVisible] = useState(false);
  const poiName = model.poi === "home" ? "家" : "公司";
  const reminder = model.mode === "manual"
    ? model.pendingCount > 0 ? `你有 ${model.pendingCount} 件待取快递`
      : model.inTransitCount > 0 ? `你有 ${model.inTransitCount} 件快递正在运输中` : "你目前没有待取快递"
    : `你在${poiName}附近有 ${model.pendingCount} 件待取快递`;

  useEffect(() => {
    if (!model.ttsText) return;
    setCaptionVisible(true);
    const timer = window.setTimeout(() => setCaptionVisible(false), 8000);
    return () => window.clearTimeout(timer);
  }, [model.ttsSequence, model.ttsText]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (model.surface === "card") onEvent({ type: "COLLAPSE" });
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [model.surface, onEvent]);

  return (
    <main className="desktop-stage desktop-stage--standard desktop-stage--figma-replica parcel-day-desktop">
      <CarScreenViewport>
        <div className="parcel-day-desktop__canvas" onClickCapture={(event) => {
          if ((event.target as HTMLElement).closest("[data-parcel-interactive]")) return;
          if (model.surface === "card") onEvent({ type: "COLLAPSE" });
        }}>
          <img className="parcel-day-desktop__background" src={assetUrl("assets/figma/traditional-day-parcel.png")} alt="白天传统桌面，左侧车辆环境，右侧浅色地图" draggable={false} />
          <div className="parcel-day-desktop__status" aria-label="车机状态栏">
            <div className="parcel-day-desktop__indicators"><MoreHorizontal /><Bell /><Bluetooth /><Signal /><Wifi /><span>18:30</span></div>
          </div>
          <span className={`parcel-day-desktop__gear${model.gear === "P" ? " is-parked" : ""}`} aria-label={`车辆${model.gear}档`}>{model.gear}</span>
          {model.surface === "capsule" ? (
            <button data-parcel-interactive type="button" className="parcel-day-desktop__capsule" onClick={() => onEvent({ type: "OPEN" })} aria-label={`展开提醒：${reminder}`}>
              <img src={assetUrl("assets/figma/parcel-eva-head.svg")} alt="" />
              <span>{reminder}</span>
            </button>
          ) : null}
          {model.phase === "querying" ? <div className="parcel-day-desktop__query" role="status"><span />正在查询{model.queryReason === "manual" ? "我的快递" : `${poiName}附近的待取快递`}…</div> : null}
          {model.surface === "card" ? (
            <div data-parcel-interactive>
              <ParcelDayReminderCard
                title="快递助手"
                description={reminder}
                onIgnore={() => onEvent({ type: "IGNORE" })}
              />
            </div>
          ) : null}
          {captionVisible && model.ttsText ? (
            <div className="parcel-day-desktop__speech" role="status"><Volume2 size={30} /><span>{model.ttsText}</span></div>
          ) : null}
        </div>
      </CarScreenViewport>
    </main>
  );
}
